// markdown-it 15 ships its own types (@types/markdown-it is gone) and the
// default export is now the callable factory, not the instance type — so the
// instance type is imported by name.
import MarkdownIt, { type MarkdownIt as MarkdownItInstance } from "markdown-it";
import markdownItLinkAttributes from "markdown-it-link-attributes";
import { isSafari } from "react-device-detect";

import { BW_EMOJI_ALT, BW_EMOJI_DOM_ALT } from "./bw-emoji";

// Plugin mentions format @[Name](userId:...,email:...)
function mentionPlugin(md: MarkdownItInstance) {
  md.inline.ruler.before("link", "mention", (state, silent) => {
    const match = state.src
      .slice(state.pos)
      .match(/^@\[(.+?)\]\(userId:([^,]+),email:([^)]+)\)/);

    if (!match) return false;
    if (silent) return true; // if only check syntax, no need to change state

    const [fullMatch, label, userId, email] = match;

    // create new token for mention
    const token = state.push("mention", "", 0);
    token.attrs = [
      ["data-type", "mention"],
      ["data-id", userId],
      ["data-label", label],
      ["data-email", email],
      ["class", "mention"],
      ["data-model", "user"],
      ["contenteditable", "false"],
    ];
    token.content = `@${label}`;

    // update parser position
    state.pos += fullMatch.length;
    return true;
  });

  // Renderer for mention. The captures are user-controlled (any client — including
  // the V4 mobile app — can put arbitrary text in a message body), and this custom
  // rule bypasses markdown-it's own escaping, so every value AND the content must be
  // escaped here or a crafted mention is stored XSS for every reader of the thread.
  md.renderer.rules.mention = (tokens, idx) => {
    const token = tokens[idx];
    const attrs = token.attrs
      ?.map(([key, value]) => `${key}="${md.utils.escapeHtml(String(value))}"`)
      .join(" ");
    return `<span ${attrs}>${md.utils.escapeHtml(token.content)}</span>`;
  };
}

/**
 * Only sources a custom emoji can legitimately have: the bundled data-URI image, or
 * an https asset. Anything else (`javascript:`, attribute-breakout attempts, …) is
 * user-crafted markdown, not an emoji — rendered as plain text by not tokenizing.
 *
 * The `data:image/` branch is LOAD-BEARING and must never be narrowed: every emoji
 * ever stored — including the 5,962-char pre-shrink bodies — is a data URI, and
 * dropping it would blank the emoji out of all of them. `https://` is kept for the
 * same reason: it costs nothing and a hosted era would otherwise be unreadable.
 */
const SAFE_EMOJI_SRC = /^(data:image\/|https:\/\/)/;

/** Built from the shared token so the writer and the reader cannot drift apart. */
const BW_EMOJI_TOKEN = new RegExp(`^!\\[${BW_EMOJI_ALT}\\]\\((.+?)\\)`);

function emojiPlugin(md: MarkdownItInstance) {
  md.inline.ruler.before("image", "emoji", (state, silent) => {
    const match = state.src.slice(state.pos).match(BW_EMOJI_TOKEN);

    if (!match) return false;
    if (silent) return false;

    const [fullMatch, src] = match;
    if (!SAFE_EMOJI_SRC.test(src)) return false;

    const token = state.push("emoji", "img", 0);
    token.attrs = [
      ["src", src],
      ["alt", BW_EMOJI_DOM_ALT],
      [
        "class",
        `inline-block relative -top-px ${isSafari ? "size-5" : "size-4"}`,
      ],
      ["contenteditable", "false"],
      ["draggable", "false"],
    ];
    token.content = ""; // img tag doesn't need content

    state.pos += fullMatch.length;
    return true;
  });

  // Same escaping obligation as the mention renderer: `src` is a capture from
  // user-controlled markdown and this rule bypasses markdown-it's escaping.
  md.renderer.rules.emoji = (tokens, idx) => {
    const token = tokens[idx];
    const attrs = token.attrs
      ?.map(([key, value]) => `${key}="${md.utils.escapeHtml(String(value))}"`)
      .join(" ");
    return `<img ${attrs} />`;
  };
}

/**
 * GFM's `- [x]` / `- [ ]` marker. Markdown-it's default preset does not implement task
 * lists, so without the rule below the marker survives only as literal text.
 */
const TASK_MARKER = /^\[([ xX])\]\s+/;

/** One markdown-it token, without reaching into the package's internal type paths. */
type MarkdownToken = ReturnType<MarkdownItInstance["parse"]>[number];

/**
 * Stamps the `data-type` / `data-checked` markers Tiptap's TaskList and TaskItem parse
 * rules match, so a pasted `- [x] done` becomes a real checkbox rather than a bullet whose
 * text happens to start with a bracket.
 *
 * Opt-in (see `MarkdownToHtmlOptions.taskLists`): an editor with no taskItem node is better
 * served by the literal `[x]` text, which at least records the state.
 */
function taskListPlugin(md: MarkdownItInstance) {
  md.core.ruler.after("inline", "task_list", (state) => {
    const { tokens } = state;
    const taskLists = new Set<MarkdownToken>();

    tokens.forEach((token, index) => {
      if (token.type !== "inline") return;
      const marker = token.content.match(TASK_MARKER);
      if (!marker) return;

      // `paragraph_open` sits between the two, and it is `hidden` in a tight list — which
      // is why the item is at -2 rather than -1.
      const itemOpen = tokens[index - 2];
      if (itemOpen?.type !== "list_item_open") return;

      // An ordered list is left alone: `1. [x] foo` is not a task list in GFM either.
      const listOpen = enclosingListOpen(tokens, index - 3);
      if (listOpen?.type !== "bullet_list_open") return;

      itemOpen.attrSet("data-type", "taskItem");
      itemOpen.attrSet("data-checked", String(marker[1].toLowerCase() === "x"));
      taskLists.add(listOpen);

      // Drop the marker from both the token and the text child the renderer reads.
      token.content = token.content.slice(marker[0].length);
      const firstChild = token.children?.[0];
      if (firstChild?.type === "text") {
        firstChild.content = firstChild.content.replace(TASK_MARKER, "");
      }
    });

    taskLists.forEach((listOpen) => markTaskList(tokens, listOpen));
  });
}

/** The `*_list_open` enclosing the item that ends at `from`, skipping closed siblings. */
function enclosingListOpen(
  tokens: MarkdownToken[],
  from: number,
): MarkdownToken | null {
  let depth = 0;

  for (let index = from; index >= 0; index--) {
    const token = tokens[index];
    if (token.type.endsWith("_list_close")) {
      depth++;
    } else if (token.type.endsWith("_list_open")) {
      if (depth === 0) return token;
      depth--;
    }
  }

  return null;
}

/**
 * Marks the list, and every one of its direct items, as a task list.
 *
 * Marking only the items that carried a `[x]` would leave a MIXED list — and `taskList`'s
 * content is `taskItem+`, so ProseMirror cannot parse one: it closes the task list early
 * (auto-filling a blank item, which surfaces as an empty checkbox), emits the plain bullet
 * as its own list, then opens a second task list. Coercing the plain items to unchecked
 * keeps one list and loses nothing but the bullet's bullet-ness.
 */
function markTaskList(tokens: MarkdownToken[], listOpen: MarkdownToken): void {
  listOpen.attrSet("data-type", "taskList");

  let depth = 0;

  for (
    let index = tokens.indexOf(listOpen) + 1;
    index < tokens.length;
    index++
  ) {
    const token = tokens[index];

    if (token.type.endsWith("_list_open")) {
      depth++;
    } else if (token.type.endsWith("_list_close")) {
      if (depth === 0) return;
      depth--;
    } else if (depth === 0 && token.type === "list_item_open") {
      if (token.attrGet("data-type")) continue;
      token.attrSet("data-type", "taskItem");
      token.attrSet("data-checked", "false");
    }
  }
}

export type MarkdownToHtmlOptions = {
  /**
   * Emit `- [x]` items as Tiptap `taskList` / `taskItem` markup instead of leaving the
   * marker as text. Off by default, and deliberately so: the rich-text composer registers
   * no taskItem node, and `components/ui/markdown.tsx` renders stored message bodies whose
   * appearance must not change.
   */
  taskLists?: boolean;
  /**
   * Render the source as an INLINE run — emphasis, links, mentions and emoji — with no
   * block markup around it: no `<p>`, no list, no heading. Block syntax is left as the
   * literal text it was written as, which is what a one-line summary wants anyway.
   *
   * For a caller that draws markdown INSIDE a line of its own text, such as an inbox
   * row's preview. A `<p>` there is an atomic block in the middle of an inline run: it
   * breaks `truncate` (`text-overflow: ellipsis` drops a block-level box whole rather
   * than clipping it, so the line renders as nothing but the ellipsis) and nests a
   * block inside the caller's `<p>`.
   */
  inline?: boolean;
};

export function markdownToHtml(
  markdown: string,
  options: MarkdownToHtmlOptions = {},
): string {
  if (!markdown) return "";
  const md = new MarkdownIt({
    html: false,
    linkify: true,
    typographer: true,
  });

  // markdown-it 15 pulled in linkify-it 6, which turned `fuzzyLink` OFF by
  // default. Without this, a bare `example.com` / `www.example.com` typed in
  // the editor silently stops rendering as a link — behaviour every earlier
  // version had. Restored explicitly rather than inherited from a default.
  md.linkify.set({ fuzzyLink: true });

  md.use(mentionPlugin)
    .use(emojiPlugin)
    .use(markdownItLinkAttributes, {
      attrs: {
        target: "_blank", // Open links in new tab
        rel: "noopener noreferrer", // Security best practices
      },
    });

  if (options.taskLists) {
    md.use(taskListPlugin);
  }

  // `renderInline` skips the block ruler entirely, so the inline plugins above still
  // run and nothing else wraps their output.
  return options.inline ? md.renderInline(markdown) : md.render(markdown);
}
